import { Button } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateDesignFolder } from '~/api/design-folders';
import {
  Dialog,
  DialogContentTitle,
  DialogForm,
  DialogTitle,
} from '~/components/dialogs/components';
import {
  DialogActions,
  DialogContent,
  DialogFieldLabel,
  DialogFieldRequired,
} from '~/components/dialogs/lib/styles';
import { DetailTextField } from '~/components/forms/details';
import { useDesignFiltersParams } from '../context/DesignFiltersContext/searchParams';

interface AddFolderDialogProps {
  designFolderId: number;
  openNewFolderDialog: boolean;
  setOpenNewFolderDialog: (val: boolean) => void;
}

export const AddFolderDialog = ({
  designFolderId,
  openNewFolderDialog,
  setOpenNewFolderDialog, // refetch,
}: AddFolderDialogProps) => {
  const { t } = useTranslation();
  const [folderNameTouched, setFolderNameTouched] = useState(false);

  const [folderName, setFolderName] = useState('');

  const folderNameValid = folderName.trim() !== '';

  const [create, createState] = useCreateDesignFolder();
  const { tags, published } = useDesignFiltersParams();

  const handleClose = () => {
    setOpenNewFolderDialog(false);
    setFolderNameTouched(false);
    setFolderName('');
    createState.reset();
  };

  const onAddFolder = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await create({
      variables: {
        condition: { tags, published },
        input: { name: folderName, parentId: designFolderId },
      },
    });
    handleClose();
  };

  return (
    <Dialog
      open={openNewFolderDialog}
      onClose={handleClose}
      aria-label={t('pages.studio.dialogs.add_folder.title')}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle onClose={handleClose}>{t('pages.studio.dialogs.add_folder.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>{t('pages.studio.dialogs.add_folder.description')}</DialogContentTitle>
        <DialogFieldLabel>
          {t('pages.studio.dialogs.add_folder.folder_name')}
          <DialogFieldRequired>*</DialogFieldRequired>
        </DialogFieldLabel>
        <DialogForm id="new-folder" onSubmit={onAddFolder}>
          <DetailTextField
            autoFocus
            placeholder={t('pages.studio.dialogs.add_folder.placeholder')}
            onBlur={() => setFolderNameTouched(true)}
            error={folderNameTouched && !folderNameValid}
            value={folderName}
            onChange={(event) => setFolderName(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          disabled={!folderNameValid || createState.loading}
          form="new-folder"
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
